import { useState } from "react";

import { faEnvelope, faLockKeyhole } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import type { z } from "zod";

import { authenticateWithEmail, signupWithEmail } from "routes/login/queries";
import { getErrorMessage, signupSchema } from "routes/login/utils/validation/emailPasswordSchemas";
import { StyledForm } from "routes/onboarding/views/AboutYou";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { LOGIN_URL } from "utils/auth";
import { getAppRawDataRegion } from "utils/region";

import { PageContainer } from "./PageContainer";

type FormValues = z.infer<typeof signupSchema>;

export const EmailSignup = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const theme = useTheme();
  const [authRequest, setAuthRequest] = useState({ loading: false, error: false, message: "" });
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    mode: "onBlur",
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: new URLSearchParams(search).get("e") || "",
    },
  });
  const confirmationToken = new URLSearchParams(search).get("t");

  const onSubmit: SubmitHandler<FormValues> = async body => {
    setAuthRequest({ loading: true, error: false, message: "" });
    try {
      await signupWithEmail({
        ...body,
        confirmation_token: confirmationToken as string,
        country_code: getAppRawDataRegion(),
      });

      await authenticateWithEmail({ email: body.email, password: body.password });

      navigate("/onboarding");
    } catch (error) {
      if (error != null && axios.isAxiosError(error)) {
        setAuthRequest({
          loading: false,
          error: true,
          message: error?.response?.status === 401 ? t("errors.invalid_login") : t("errors.generic"),
        });
      }
    }
  };

  return (
    <PageContainer>
      <Typography variant="h2">{t("signup.info.email.title")}</Typography>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} style={{ marginBottom: "16px" }}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField
                {...field}
                type="email"
                disabled
                label={t("form.email")}
                value={field.value || ""}
                error={!!errors.email}
                helperText={getErrorMessage(t, errors.email)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    "data-testid": "signup_email_form_email",
                  },
                }}
                sx={{
                  bgcolor: theme.colors.redesign.db10,
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: theme.colors.redesign.db100,
                    "-webkit-text-fill-color": "initial",
                  },
                }}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <TextField
                {...field}
                type="password"
                label={t("form.password")}
                value={field.value || ""}
                error={!!errors.password}
                helperText={getErrorMessage(t, errors.password)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon icon={faLockKeyhole} />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    "data-testid": "signup_email_form_password",
                  },
                }}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="password_confirmation"
            render={({ field }) => (
              <TextField
                {...field}
                type="password"
                label={t("form.confirm_password")}
                value={field.value || ""}
                error={!!errors.password_confirmation}
                helperText={getErrorMessage(t, errors.password_confirmation)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon icon={faLockKeyhole} />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    "data-testid": "signup_email_form_confirm_password",
                  },
                }}
                fullWidth
              />
            )}
          />
        </Stack>

        {authRequest.error && (
          <Notification variant="danger" style={{ marginTop: "16px", marginBottom: "16px" }}>
            {authRequest?.message}
          </Notification>
        )}
        <BaseButton
          type="submit"
          text={t("signup.info.email.button")}
          disabled={authRequest.loading}
          data-testid="signup_email_form_submit_btn"
          fullWidth
          loading={authRequest.loading}
        />

        <Box textAlign="center" mt={2}>
          <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
            {t("form.already_have_account")}{" "}
            <Link to={LOGIN_URL} style={{ color: theme.colors.redesign.db60 }}>
              {t("form.log_in")}
            </Link>
          </Typography>
        </Box>
      </StyledForm>
    </PageContainer>
  );
};
